import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, Validators, FormBuilder, FormControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
// services
import { StartupService } from '../services/startup.service';
import { SharedService } from '../shared/services/shared.service';
import { ChatbotService } from '../chatbot/chatbot.service';
import { InactivityService } from '../shared/services/inactivity.service';
import { LocalDatabaseService } from '../shared/services/local-database.service';
import { ActivatedRoute } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ChangePasswordComponent } from '../shared/modals/change-password/change-password.component';
import { ForgotPasswordComponent } from '../shared/modals/forgot-password/forgot-password.component';
import { LoginHelpComponent } from '../shared/modals/login-help/login-help.component';
// Third Party
import { Subscription } from 'rxjs';
import { finalize, switchMap, tap } from 'rxjs/operators';
import * as bcrypt from 'bcryptjs';
// Models
import { ModalID, StatusModalIndicator } from '../shared/models/constants';
import { loginAccount } from '../shared/models/company';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, NgClass, RouterModule],
})
export class LoginComponent implements OnInit, OnDestroy {
  login_form: FormGroup;
  subscriptions = new Subscription();
  isLoggingIn = false;
  private loginSpinnerGuard: ReturnType<typeof setTimeout> | null = null;
  showPassword = false;
  rememberMe = false;

  constructor(
    private fb: FormBuilder,
    public startupService: StartupService,
    public sharedService: SharedService,
    private router: Router,
    private inactivityService: InactivityService,
    private route: ActivatedRoute,
    private modalService: BsModalService,
    private chatbotService: ChatbotService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private localDb: LocalDatabaseService,
  ) {
    this.login_form = this.fb.group({
      login_email: new FormControl({ value: null, disabled: false }, [
        Validators.required,
        Validators.email,
      ]),
      login_pwd: new FormControl({ value: null, disabled: false }, [
        Validators.required,
        Validators.minLength(5),
      ]),
    });
    
    // Legacy support for user_successfully_logged_in$
    this.subscriptions.add(
      this.startupService.user_successfully_logged_in$.subscribe(
        (account: loginAccount) => {
          // Logic moved to switchMap in loginClick()
        },
      ),
    );
  }
  
  ngOnInit(): void {
    // Check for reset password token in query params
    const resetToken = this.route.snapshot.queryParamMap.get('resetToken');
    if (resetToken) {
      this.openResetPasswordModal(resetToken);
    }
    
    // Ensure chatbot is completely cleared out and closed if user signed out
    this.chatbotService.messages = [];
    this.chatbotService.isOpen = false;
  }

  private openResetPasswordModal(token: string) {
    this.modalService.show(ChangePasswordComponent, {
      initialState: {
        userId: 'RESET_TOKEN_MODE', // Signal to component to use token
        isForced: true,
        resetToken: token // Pass the token
      },
      ignoreBackdropClick: true,
      keyboard: false,
      class: 'modal-dialog-centered'
    });
  }

  loginClick() {
    if (this.login_form.invalid) return;

    if (this.loginSpinnerGuard) {
      clearTimeout(this.loginSpinnerGuard);
      this.loginSpinnerGuard = null;
    }

    this.isLoggingIn = true;
    this.cdr.detectChanges();
    // Defensive fallback: never leave the button spinner stuck indefinitely.
    this.loginSpinnerGuard = setTimeout(() => {
      this.resetLoginUiState();
    }, 30000);

    const email = (this.login_form.get('login_email')?.value ?? '').trim();
    const password = this.login_form.get('login_pwd')?.value;

    this.subscriptions.add(
      this.startupService
        .getVerifyCredentials(email, password, this.rememberMe)
        .pipe(finalize(() => this.resetLoginUiState()))
        .subscribe({
        next: (account: loginAccount) => {
          // Ensure the chatbot closes when transitioning to the dashboard
          this.chatbotService.isOpen = false;

          // Crucial: Set the logged_in_user state IMMEDIATELY so the AuthGuard allows navigation
          this.sharedService.logged_in_user = { 
            user_id: account.user_id || account.id || 'valid-id-for-guard',
            email: account.email,
            username: account.username,
            role: account.role,
            company_id: account.company_id,
            name_first: account.name_first,
            name_last: account.name_last,
            must_change_password: account.must_change_password || false,
          } as any;

          this.sharedService.raiseSuccessToast('Log in successful');
          this.inactivityService.startWatching();

          // Emit the login event so app.component.ts can initialize post-login services (like fetching company data)
          this.startupService.user_successfully_logged_in$.next(account);

          // Cache minimal non-PII fields BEFORE navigating so AuthGuard can
          // restore the session synchronously when it fires on the next route.
          localStorage.setItem('cached_user', JSON.stringify({
            user_id: this.sharedService.logged_in_user.user_id,
            role: (this.sharedService.logged_in_user as any).role,
            company_id: (this.sharedService.logged_in_user as any).company_id,
            username: (this.sharedService.logged_in_user as any).username,
            must_change_password: (this.sharedService.logged_in_user as any).must_change_password || false,
          }));

          // Cache credentials for offline login
          this.cacheCredentialsForOffline(email, password, account);

          // Navigate directly to the dashboard. The setup-checklist is still
          // accessible from the sidenav for accounts that need onboarding.
          this.router.navigate(['/home']);
          
        },
        error: (err) => {
          // If the server is unreachable, try offline login from cached credentials
          if (this.isNetworkError(err)) {
            this.attemptOfflineLogin(email, password);
          } else {
            this.resetLoginUiState();
            // Error toast is already handled by startupService
          }
        }
      })
    );
  }

  private resetLoginUiState(): void {
    this.ngZone.run(() => {
      this.isLoggingIn = false;
      if (this.loginSpinnerGuard) {
        clearTimeout(this.loginSpinnerGuard);
        this.loginSpinnerGuard = null;
      }
      this.cdr.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.resetLoginUiState();
    this.subscriptions.unsubscribe();
  }
  
  togglePassword() {
    this.showPassword = !this.showPassword;
  }
  
  forgotPasswordClick() {
    this.modalService.show(ForgotPasswordComponent, {
      class: 'modal-dialog-centered'
    });
  }
  
  helpWithLoginClick() {
    this.modalService.show(LoginHelpComponent, {
      class: 'modal-dialog-centered'
    });
  }

  /** Detect network-level errors (server unreachable, offline, timeout). */
  private isNetworkError(err: any): boolean {
    if (!navigator.onLine) return true;
    if (err instanceof HttpErrorResponse) {
      return err.status === 0 || err.status >= 500;
    }
    const msg = (err?.message || '').toLowerCase();
    return msg.includes('network error') || msg.includes('failed to fetch') || msg.includes('timed out');
  }

  /** After a successful online login, hash the password and cache it in IndexedDB. */
  private async cacheCredentialsForOffline(email: string, password: string, account: loginAccount): Promise<void> {
    try {
      const passwordHash = bcrypt.hashSync(password, 10);
      await this.localDb.cachedCredentials.put({
        email: email.toLowerCase(),
        passwordHash,
        user_id: account.user_id || account.id || '',
        company_id: account.company_id,
        username: account.username || email,
        role: account.role || 'User',
        name_first: account.name_first,
        name_last: account.name_last,
      });
    } catch (e) {
      console.warn('Failed to cache credentials for offline login:', e);
    }
  }

  /** Attempt to log in using cached credentials from IndexedDB when the server is unreachable. */
  private async attemptOfflineLogin(email: string, password: string): Promise<void> {
    try {
      const cached = await this.localDb.cachedCredentials.get(email.toLowerCase());
      if (!cached) {
        this.sharedService.raiseErrorToast("You haven't logged in on this device before, so offline login isn't available yet. Please connect to the internet and try again.");
        this.resetLoginUiState();
        return;
      }

      const isValid = bcrypt.compareSync(password, cached.passwordHash);
      if (!isValid) {
        this.sharedService.raiseErrorToast('Email or password is incorrect.');
        this.resetLoginUiState();
        return;
      }

      // Build login account from cached data
      const account = new loginAccount();
      account.email = cached.email;
      account.user_id = cached.user_id;
      account.company_id = cached.company_id;
      account.username = cached.username;
      account.role = cached.role;
      account.name_first = cached.name_first;
      account.name_last = cached.name_last;

      // Ensure the chatbot closes when transitioning to the dashboard
      this.chatbotService.isOpen = false;

      // Set the logged_in_user state so the AuthGuard allows navigation
      this.sharedService.logged_in_user = {
        user_id: cached.user_id,
        email: cached.email,
        username: cached.username,
        role: cached.role,
        company_id: cached.company_id,
        name_first: cached.name_first,
        name_last: cached.name_last,
      } as any;

      this.sharedService.raiseSuccessToast('Logged in offline');
      this.inactivityService.startWatching();

      // Emit the login event so app.component.ts can initialize post-login services
      this.startupService.user_successfully_logged_in$.next(account);

      // Cache for AuthGuard
      localStorage.setItem('cached_user', JSON.stringify({
        user_id: cached.user_id,
        role: cached.role,
        company_id: cached.company_id,
        username: cached.username,
      }));

      this.resetLoginUiState();
      this.router.navigate(['/home']);
    } catch (e) {
      console.error('Offline login failed:', e);
      this.sharedService.raiseErrorToast('The server is unavailable and offline login failed.');
      this.resetLoginUiState();
    }
  }
}
